import { action } from '@play-co/replicant';
import * as Inventory from '../inventory';
import createActions from '../config/actions/createActions';
import {
  incrementStreakBattleRoyaleCompleteCount,
  resetStreakBattleRoyale,
  setStreakBattleRoyaleIntroSeen,
  setStreakBattleRoyaleLastMilestoneSeen,
  setStreakBattleRoyaleStatus,
  joinStreakBattleRoyale,
} from './streakbattleroyale.modifiers';
import {
  getStreakBattleRoyaleActiveTheme,
  getStreakBattleRoyaleCurrentMilestone,
  getStreakBattleRoyaleGoal,
  getStreakBattleRoyalePlayerCount,
  getStreakBattleRoyaleRemainingTime,
  getStreakBattleRoyaleRewardGemsPool,
  getStreakBattleRoyaleStatus,
  getStreakBattleRoyaleTotalLevelStreak,
  isStreakBattleRoyaleExpired,
} from './streakbattleroyale.getters';
import { duration } from '../utils/duration';
import { sendAnalyticsEvents } from '../analytics';

export const streakBattleRoyaleActions = createActions({
  /**
   * Sets the streak event intro seen flag.
   */
  setStreakBattleRoyaleIntroSeen: action((state) => {
    setStreakBattleRoyaleIntroSeen(state);
  }),

  /**
   * Cache the last milestone seen on the streak event state.
   */
  setStreakBattleRoyaleLastMilestoneSeen: action((state, milestone: number) => {
    setStreakBattleRoyaleLastMilestoneSeen(state, milestone);
  }),

  /**
   * Join the streak event.
   */
  joinStreakBattleRoyale: action((state, _: void, api) => {
    const now = api.date.now();
    const success = joinStreakBattleRoyale(state, now);
    if (!success) return;

    api.scheduledActions.schedule.sendBattleRoyaleEndingMessage({
      args: {
        subFeature: 'notification_battle_royale_ending',
      },
      notificationId: 'notification_battle_royale_ending',
      delayInMS:
        getStreakBattleRoyaleRemainingTime(state, now) - duration({ hours: 1 }),
    });

    sendAnalyticsEvents(api, [
      {
        eventType: 'StreakQuestStart',
        eventProperties: {
          theme: getStreakBattleRoyaleActiveTheme(state),
          progression: state.streak?.progression ?? 'basic',
          preStreak: getStreakBattleRoyaleTotalLevelStreak(state, now),
        },
      },
    ]);
  }),

  /**
   * Resets the streak event. Hard-resetting if the event is expired.
   */
  resetStreakBattleRoyale: action((state, _: void, api) => {
    const now = api.date.now();
    const isExpired = isStreakBattleRoyaleExpired(state, now);
    const theme = getStreakBattleRoyaleActiveTheme(state);
    const progression = state.streak?.progression ?? 'basic';
    const remainingPlayers = getStreakBattleRoyalePlayerCount(state);

    resetStreakBattleRoyale(state, isExpired ? api.date.now() : undefined);

    // Track event end analytics.
    sendAnalyticsEvents(api, [
      {
        eventType: 'StreakQuestEnd',
        eventProperties: {
          theme,
          progression,
          result: isExpired ? 'expired' : 'failed',
          streak: getStreakBattleRoyaleCurrentMilestone(state, now),
          totalStreak: getStreakBattleRoyaleTotalLevelStreak(state, now),
          usersCount: remainingPlayers,
        },
      },
    ]);
  }),

  /**
   * Grants the completed streak event reward.
   */
  grantStreakBattleRoyaleReward: action((state, _: void, api) => {
    let now = api.date.now();

    if (
      getStreakBattleRoyaleStatus(state) !== 'completed' ||
      isStreakBattleRoyaleExpired(state, now)
    ) {
      throw new Error(
        'Streak event is not completed, expired or already claimed',
      );
    }

    const goal = getStreakBattleRoyaleGoal(state);
    const remainingPlayers = getStreakBattleRoyalePlayerCount(state, goal);
    const pool = getStreakBattleRoyaleRewardGemsPool(state);
    const reward = Math.round(pool / remainingPlayers);
    Inventory.modifiers.addFreeGems(
      state,
      reward,
      { feature: 'streak_battle_royale' },
      api,
    );
    setStreakBattleRoyaleStatus(state, 'rewarded', now);

    /**
     * Increment the streak event total complete count.
     * Currently used for determining the player counts on the goal milestone for player's first win,
     * hence the increment is done after the reward is granted.
     */
    incrementStreakBattleRoyaleCompleteCount(state);

    // Track  event end analytics.
    sendAnalyticsEvents(api, [
      {
        eventType: 'StreakQuestEnd',
        eventProperties: {
          theme: getStreakBattleRoyaleActiveTheme(state),
          progression: state.streak?.progression ?? 'basic',
          result: 'success',
          streak: getStreakBattleRoyaleCurrentMilestone(state, now),
          totalStreak: getStreakBattleRoyaleTotalLevelStreak(state, now),
          usersCount: remainingPlayers,
        },
      },
    ]);
  }),
});
